import { useEffect, useState } from "react";
import api, { API, TOKEN_KEY } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { MonitorPlay, Copy, ExternalLink, Volume2, VolumeX, Gamepad2, Sparkles, SlidersHorizontal, Save, Play } from "lucide-react";
import { toast } from "sonner";
import SoundPicker from "@/components/SoundPicker";

function OverlayCard({ title, desc, url, icon: Icon }) {
  const copy = () => { navigator.clipboard.writeText(url); toast.success("URL copied — paste into an OBS Browser Source"); };
  return (
    <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-5">
      <div className="flex items-center gap-2 mb-2"><Icon className="w-4 h-4 text-primary" /><h3 className="font-heading font-700 text-sm">{title}</h3></div>
      <p className="text-xs text-muted-foreground mb-3">{desc}</p>
      <div className="flex gap-2">
        <code className="flex-1 text-xs bg-secondary rounded-lg px-3 py-2 truncate text-cookie" data-testid={`overlay-url-${title}`}>{url}</code>
        <Button size="sm" onClick={copy} className="bg-primary hover:bg-primary/90" data-testid={`copy-${title}`}><Copy className="w-4 h-4" /></Button>
        <a href={url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="border-white/10"><ExternalLink className="w-4 h-4" /></Button></a>
      </div>
    </div>
  );
}

export default function OverlayInfo() {
  const [tts, setTts] = useState({ enabled: false, voice: "en-US-AnaNeural" });
  const [audio, setAudio] = useState(null);
  const [testing, setTesting] = useState(false);
  const [overlayKey, setOverlayKey] = useState("");
  useEffect(() => {
    api.get("/tts/status").then((r) => setTts(r.data)).catch(() => {});
    api.get("/config/audio").then((r) => setAudio(r.data)).catch(() => {});
    api.get("/overlay/key").then((r) => setOverlayKey(r.data.key || "")).catch(() => {});
  }, []);
  const base = window.location.origin;
  const kq = overlayKey ? `?key=${encodeURIComponent(overlayKey)}` : "";

  const saveAudio = async () => {
    const r = await api.post("/config/audio", audio);
    setAudio(r.data);
    toast.success("Audio settings saved");
  };

  const testVoice = async () => {
    setTesting(true);
    try {
      await saveAudio();
      const res = await fetch(`${API}/tts/speak`, {
        method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
        body: JSON.stringify({ text: "Hi hi hi! This is what I sound like right now! Hehe!" }),
      });
      if (res.status === 204) { toast.error("Add your Azure region to hear the voice"); return; }
      const blob = await res.blob();
      await new Audio(URL.createObjectURL(blob)).play();
    } catch (e) { toast.error("Test failed"); }
    finally { setTesting(false); }
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl md:text-4xl font-800 font-heading">OBS Overlay</h1>
        <p className="text-muted-foreground mt-1 text-sm">Add these as Browser Sources in OBS. Music, PancakeCupCake_'s voice, and game pop-ups all play here.</p>
      </header>

      <div className={`rounded-lg p-4 flex items-center gap-3 border ${tts.enabled ? "border-green-500/40 bg-green-500/10" : "border-yellow-500/40 bg-yellow-500/10"}`} data-testid="tts-status">
        {tts.enabled ? <Volume2 className="w-5 h-5 text-green-400" /> : <VolumeX className="w-5 h-5 text-yellow-400" />}
        <div className="text-sm">
          {tts.enabled
            ? <>Azure TTS is <span className="text-green-400 font-semibold">active</span> — voice <span className="text-white">{tts.voice}</span>.</>
            : <>Azure TTS is <span className="text-yellow-400 font-semibold">not active yet</span>. Add your <span className="text-white">Azure region</span> in the backend to enable {tts.voice}. Everything else works; she just won't speak aloud.</>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {overlayKey ? (
          <>
            <OverlayCard title="Stream Overlay" icon={MonitorPlay} url={`${base}/overlay${kq}`} desc="Full overlay: music (with ducking when she talks), her voice, and game pop-ups. This is a private link — keep it secret. Recommended size 1920x1080." />
            <OverlayCard title="Games Only" icon={Gamepad2} url={`${base}/overlay/games${kq}`} desc="Just the animated game feed (fishing, gambling, gifts) — no audio. Private link. Great as a separate corner source." />
          </>
        ) : (
          <div className="text-xs text-muted-foreground" data-testid="overlay-url-loading">Loading your private overlay links…</div>
        )}
      </div>

      <div className="rounded-lg p-4 border border-primary/30 bg-primary/10 text-xs text-muted-foreground" data-testid="overlay-key-note">
        These overlay links include a <span className="text-white">private key</span> tied to your account. Anyone without the exact link sees a locked screen, so paste them only into your own OBS.
      </div>

      {/* Audio tuner */}
      {audio && (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-6" data-testid="audio-settings">
          <div className="flex items-center gap-2 mb-1"><SlidersHorizontal className="w-4 h-4 text-primary" /><h3 className="font-heading font-700 text-lg">Voice & Audio Tuner</h3></div>
          <p className="text-xs text-muted-foreground mb-5">Adjust how PancakeCupCake_ sounds and how loud the music is versus her voice.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
            <div>
              <div className="flex justify-between text-xs mb-2"><span className="text-muted-foreground">Voice speed</span><span className="text-primary">{audio.voice_rate > 0 ? "+" : ""}{audio.voice_rate}%</span></div>
              <Slider data-testid="voice-rate" value={[audio.voice_rate]} min={-50} max={50} step={1} onValueChange={(v) => setAudio((a) => ({ ...a, voice_rate: v[0] }))} />
            </div>
            <div>
              <div className="flex justify-between text-xs mb-2"><span className="text-muted-foreground">Voice pitch</span><span className="text-primary">{audio.voice_pitch > 0 ? "+" : ""}{audio.voice_pitch} st</span></div>
              <Slider data-testid="voice-pitch" value={[audio.voice_pitch]} min={-20} max={20} step={1} onValueChange={(v) => setAudio((a) => ({ ...a, voice_pitch: v[0] }))} />
            </div>
            <div>
              <div className="flex justify-between text-xs mb-2"><span className="text-muted-foreground">Music volume when she talks (ducking)</span><span className="text-cookie">{audio.duck_volume}%</span></div>
              <Slider data-testid="duck-volume" value={[audio.duck_volume]} min={0} max={100} step={5} onValueChange={(v) => setAudio((a) => ({ ...a, duck_volume: v[0] }))} />
            </div>
            <div>
              <div className="flex justify-between text-xs mb-2"><span className="text-muted-foreground">Normal music volume</span><span className="text-cookie">{audio.music_volume}%</span></div>
              <Slider data-testid="music-volume" value={[audio.music_volume]} min={0} max={100} step={5} onValueChange={(v) => setAudio((a) => ({ ...a, music_volume: v[0] }))} />
            </div>
            <div className="flex items-center justify-between rounded-lg bg-secondary px-4 py-3">
              <div><span className="text-sm">Alert chime</span><p className="text-[11px] text-muted-foreground">Play a chime on game pop-ups</p></div>
              <Switch checked={audio.chime_enabled} onCheckedChange={(v) => setAudio((a) => ({ ...a, chime_enabled: v }))} data-testid="chime-toggle" />
            </div>
            <div>
              <div className="flex justify-between text-xs mb-2"><span className="text-muted-foreground">Chime volume</span><span className="text-primary">{audio.chime_volume}%</span></div>
              <Slider data-testid="chime-volume" value={[audio.chime_volume]} min={0} max={100} step={5} onValueChange={(v) => setAudio((a) => ({ ...a, chime_volume: v[0] }))} />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={testVoice} disabled={testing} className="border-white/10 rounded-lg" data-testid="test-voice-button"><Play className="w-4 h-4 mr-2" />{testing ? "Playing…" : "Test Voice"}</Button>
            <Button onClick={saveAudio} className="bg-primary hover:bg-primary/90 rounded-lg" data-testid="save-audio-button"><Save className="w-4 h-4 mr-2" />Save</Button>
          </div>
        </div>
      )}

      {/* Game notification sounds */}
      {audio && (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-6" data-testid="event-sounds-panel">
          <div className="flex items-center gap-2 mb-1"><Gamepad2 className="w-4 h-4 text-cookie" /><h3 className="font-heading font-700 text-lg">Game Notification Sounds</h3></div>
          <p className="text-xs text-muted-foreground mb-5">Upload a custom sound for each event. Plays on the overlay when it happens (leave blank to use the default chime).</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[["fish", "🎣 Fishing"], ["slots", "🎰 Slots / Gambling"], ["gift", "🎁 Gift / Redeem"], ["raid", "🎉 Raid"], ["mc_gift", "⛏️ Minecraft gift alert"]].map(([k, label]) => (
              <div key={k}>
                <label className="text-xs text-muted-foreground">{label}</label>
                <div className="mt-1">
                  <SoundPicker
                    value={audio.event_sounds?.[k] || ""}
                    onChange={(v) => { const es = { ...(audio.event_sounds || {}), [k]: v }; setAudio((a) => ({ ...a, event_sounds: es })); api.post("/config/audio", { event_sounds: { [k]: v } }); }}
                    testid={`event-sound-${k}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-primary" /><h3 className="font-heading font-700 text-sm">How it works</h3></div>
        <ul className="text-sm text-muted-foreground space-y-2 list-disc pl-5">
          <li>In OBS: <span className="text-white">+ → Browser</span>, paste a URL above, set 1920×1080.</li>
          <li>PancakeCupCake_ <span className="text-white">speaks</span> when someone talks to her, on <span className="text-white">!so</span> shoutouts, and when she gives out cookies.</li>
          <li>While she talks, the <span className="text-white">music ducks</span> (gets quieter) then returns when she's done.</li>
          <li>Control music from the <span className="text-primary">Music</span> tab — play, pause, skip. It updates the overlay live.</li>
          <li>Give her treats with <span className="text-primary">!givecookies</span> / <span className="text-primary">!givecupcakes</span> and cheer her up with <span className="text-primary">!hug</span>.</li>
        </ul>
      </div>
    </div>
  );
}
